<!--================================= AI Chat Widget Start =================================-->
<!-- Browser only: auth state is rebuilt client-side from the /me cookie call, so there is nothing
     meaningful to render on the server. Guests get the same tab and panel; the panel holds a
     sign-in card instead of the conversation (see the @else below the header). -->
@if (isPlatformBrowser(platformId)) {
<div class="ai-chat-widget">

  <!--============================ Chat Panel Start ============================-->
  @if (isOpen()) {
  <section class="ai-chat-panel" id="ai-chat-panel" role="dialog" aria-modal="false"
    aria-label="Pebbl AI assistant">
    <!-- Panel Header -->
    <header class="ai-chat-panel-header">
      <!-- Named, not just badged - the same lockup the console's widget head carries, and the
           only thing identifying the assistant once the greeting has scrolled away. The
           badge and name share a wrapper because the header is justify-content: space-between,
           so a bare third child would be pushed to the middle of the bar. -->
      <div class="ai-chat-panel-brand">
        <!-- The green tile is CSS, not artwork: the asset dropped its own disc so each call site
             can shape its own ground - see ai-chat-badge-icon.svg and .ai-chat-badge. -->
        <img class="ai-chat-badge" src="/assets/ai-chat-badge-icon.svg" width="38" height="38"
          alt="" appHandleImageError loading="eager">
        <!-- The whole lockup, not half of it: badge, wordmark with its BETA chip, and the
             "by GatherCo" caption under it. The panel used to show the wordmark alone, which read
             as an unfinished mark. The BETA chip reverses an earlier decision to match the admin
             console's bare gold superscript - see the long note on .ai-chat-beta. -->
        <span class="ai-chat-panel-name">
          <span class="ai-chat-panel-word">Pebbl<sup class="ai-chat-beta">BETA</sup></span>
          <span class="ai-chat-panel-by">by GatherCo</span>
        </span>
      </div>
      <!-- Both glyphs are inline SVG on one stroke weight, not the two assets they used to be:
           the expand arrow was drawn at 1.5 on a 14 grid and the close cross at 2 on a 16 grid,
           so side by side the cross read heavier and larger than its neighbour. Same box, same
           1.6 stroke, currentColor for both, so they are one pair. -->
      <div class="ai-chat-panel-actions">
        <!-- No full page to expand into while signed out - there is no conversation yet. -->
        @if (isUserAuthenticated) {
        <a class="icon-btn ai-chat-expand-btn" [routerLink]="['/' + ROUTES.ASK_PEBBL]"
          (click)="closeChat()" aria-label="Open Pebbl AI full page" ngbTooltip="Open full page"
          placement="bottom" container="body">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
            stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M9.5 1.5H14.5V6.5M14.5 1.5 9 7M6.5 14.5H1.5V9.5M1.5 14.5 7 9" />
          </svg>
        </a>
        }
        <button class="icon-btn ai-chat-close-btn" type="button" (click)="closeChat()"
          aria-label="Close chat" ngbTooltip="Close" placement="bottom" container="body">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
            stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
            <path d="M3 3 13 13M13 3 3 13" />
          </svg>
        </button>
      </div>
    </header>

    @if (isUserAuthenticated) {
    <!-- Message List -->
    <!-- aria-busy holds the announcement back while frames are still arriving: without it a
         screen reader re-reads the reply on every one. -->
    <div class="ai-chat-messages" #messageList role="log" aria-live="polite" aria-atomic="false"
      (scroll)="onMessageListScroll()" [attr.aria-busy]="isAwaitingReply()">

      <!-- Greeting: the conversation's opening turn, not a hero. Client-side copy - it is never
           sent, stored or counted in messages() - so it stays at the top of the transcript rather
           than vanishing the moment the customer types, which is what a welcome panel does. -->
      <!-- No badge on the line itself: the panel header carries it two rows above, and a second
           copy inside the transcript made three Pebbl marks on one small panel. -->
      <p class="ai-chat-greeting">{{ greeting }}</p>

      <!-- Worked examples, under the greeting and only until the first turn - see starterPrompts
           in the conversation base for why they are back after being removed once. Each is an
           ordinary message once clicked; nothing downstream knows it started as a button. -->
      @if (!messages().length) {
      <div class="ai-chat-starters">
        <p class="ai-chat-starters-eyebrow">Try asking</p>
        <div class="ai-chat-starters-list">
          @for (prompt of starterPrompts; track prompt) {
          <button class="ai-chat-starter" type="button" (click)="useStarter(prompt)"
            [disabled]="isAwaitingReply() || !!rateLimitNotice()">
            <span>{{ prompt }}</span>
            <!-- Decorative: the button's own text is its accessible name. -->
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </button>
          }
        </div>
      </div>
      }

      @for (message of messages(); track message.id) {
        @if (message.role === 'user') {
        <div class="ai-chat-message is-user">
          @if (message.attachments?.length) {
          <div class="ai-chat-sent-attachments">
            @for (attachment of message.attachments; track attachment.attachment_uuid) {
            <!-- No url means the presigned one has not been re-minted (or could not be), so
                 render nothing rather than a broken thumbnail. -->
            @if (attachment.url) {
            <!-- A button, so the sent thumbnail opens full-size like a pending one does. -->
            <button class="ai-chat-sent-attachment-view" type="button"
              (click)="openAttachmentPreview(attachment.url, attachment.file_name)"
              [attr.aria-label]="'Preview ' + attachment.file_name"
              [ngbTooltip]="'Preview ' + attachment.file_name" placement="top" container="body">
              <img class="ai-chat-sent-attachment" [src]="attachment.url" [alt]="attachment.file_name"
                width="60" height="60" appHandleImageError loading="lazy">
            </button>
            }
            }
          </div>
          }
          @if (message.message) {
          <div class="ai-chat-user-bubble">{{ message.message }}</div>
          }
        </div>
        } @else {
        <div class="ai-chat-message is-assistant">

          <!-- Progress line: shown while the turn has produced no text yet, so a multi-second
               tool gather is never a blank bubble. Carries the tool's own label once one runs. -->
          @if (message.status_label) {
          <div class="ai-chat-analyzing">
            <img src="/assets/ai-chat-badge-icon.svg" width="24" height="24" alt=""
              appHandleImageError loading="eager">
            <span class="ai-chat-analyzing-text">{{ message.status_label }}<span
                class="ai-chat-analyzing-dots"><span>.</span><span>.</span><span>.</span></span></span>
          </div>
          }

          <!-- The reply itself: Markdown (tables, links, lists), re-rendered on every frame. Each
               entry that opens on a linked product name takes the picture of the card whose
               product_link equals that link. The cards arrive after the text, so the entries pick
               their pictures up when the frame lands. Cards with no entry (a link mid-sentence or in
               a table) sit in a labelled strip under the settled reply - only those it mentions. -->
          @if (message.message) {
          @let cardCount = message.has_error ? 0 : (message.product_skus?.length ?? 0);
          @let reply = message.message | aiReplyEntries: cardCount : (message.product_links ?? []) : (message.product_recommended ?? []);
          @for (segment of reply.segments; track $index) {
          @if (segment.entry === null) {
          <div class="ai-chat-assistant-text ai-chat-markdown"
            [class.is-streaming]="message.is_streaming && $last" [class.is-error]="message.has_error"
            [innerHTML]="segment.html"></div>
          } @else {
          <app-ai-product-entry [skus]="message.product_skus!" [index]="segment.entry ?? 0"
            [hrefs]="reply.entry_hrefs" [links]="message.product_links ?? []"
            [class.is-divided]="segment.follows_entry">
            <div class="ai-chat-assistant-text ai-chat-markdown"
              [class.is-streaming]="message.is_streaming && $last" [innerHTML]="segment.html"></div>
          </app-ai-product-entry>
          }
          }

          @if (!message.is_streaming && reply.placed < cardCount) {
          <app-ai-product-cards [skus]="message.product_skus!" [links]="message.product_links ?? []"
            [recommended]="message.product_recommended ?? []" [entryHrefs]="reply.entry_hrefs"
            [mentions]="reply.mentions" />
          }
          }

          @if (!message.is_streaming && !message.has_error) {
          <div class="ai-chat-message-actions">
            <button class="icon-btn ai-chat-action-btn" type="button" (click)="vote(message, 'up')"
              [class.is-active]="message.feedback === 'up'" [attr.aria-pressed]="message.feedback === 'up'"
              [attr.aria-label]="message.feedback === 'up' ? 'Remove good rating' : 'Good response'"
              [ngbTooltip]="message.feedback === 'up' ? 'Liked' : 'Good response'"
              placement="top" container="body">
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
                [attr.fill]="message.feedback === 'up' ? 'currentColor' : 'none'" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
            </button>
            <button class="icon-btn ai-chat-action-btn" type="button" (click)="vote(message, 'down')"
              [class.is-active]="message.feedback === 'down'" [attr.aria-pressed]="message.feedback === 'down'"
              [attr.aria-label]="message.feedback === 'down' ? 'Remove bad rating' : 'Bad response'"
              [ngbTooltip]="message.feedback === 'down' ? 'Disliked' : 'Bad response'"
              placement="top" container="body">
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
                [attr.fill]="message.feedback === 'down' ? 'currentColor' : 'none'" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
              </svg>
            </button>
            <button class="icon-btn ai-chat-action-btn" type="button" (click)="copyMessage(message)"
              aria-label="Copy reply" ngbTooltip="Copy" placement="top" container="body">
              <img src="/assets/copy.svg" width="12" height="14" alt="" appHandleImageError
                loading="eager">
            </button>
            @if (canRegenerate(message)) {
            <button class="icon-btn ai-chat-action-btn" type="button" (click)="regenerate(message)"
              [disabled]="isAwaitingReply() || !!rateLimitNotice()" aria-label="Regenerate reply"
              ngbTooltip="Regenerate" placement="top" container="body">
              <img src="/assets/ai-chat-regenerate-icon.svg" width="13" height="14" alt=""
                appHandleImageError loading="eager">
            </button>
            }
            @if (workedFor(message); as worked) {
            <span class="ai-chat-worked-for">{{ worked }}</span>
            }
          </div>
          }
        </div>
        }
      }
    </div>

    <!-- Token budget spent: the API refuses identically until the window resets, so the
         composer stays locked rather than inviting a retry that cannot succeed. -->
    @if (rateLimitNotice()) {
    <p class="ai-chat-rate-limit" role="status">{{ rateLimitNotice() }}</p>
    }

    <!-- Composer -->
    <form class="ai-chat-composer" (ngSubmit)="sendMessage()"
      (drop)="onComposerDrop($event)" (dragover)="onComposerDragOver($event)"
      (dragleave)="onComposerDragLeave()" [class.is-drag-over]="isDragOver">

      <!-- Picked images, above the field: thumbnail, upload state, remove. -->
      @if (pendingAttachments.length) {
      <div class="ai-chat-attachments">
        @for (attachment of pendingAttachments; track attachment.id) {
        <div class="ai-chat-attachment" [class.is-error]="attachment.status === 'error'">
          <!-- The tile's face is a button: the 48px thumbnail is enough to tell two picks apart,
               not to read what is in one, so a click opens it full-size. -->
          <!-- The tooltip carries what the tile's title used to: the upload error when there is
               one, the filename otherwise. -->
          <button class="ai-chat-attachment-view" type="button"
            (click)="openAttachmentPreview(attachment.preview_url, attachment.file.name)"
            [attr.aria-label]="'Preview ' + attachment.file.name"
            [ngbTooltip]="attachment.error || attachment.file.name" placement="top"
            container="body">
            <img [src]="attachment.preview_url" [alt]="attachment.file.name" width="48" height="48"
              appHandleImageError loading="eager">
          </button>
          @if (attachment.status === 'uploading') {
          <span class="ai-chat-attachment-state" aria-hidden="true"></span>
          }
          <!-- A failed upload is retried on the file already in hand - see retryAttachment. -->
          @if (attachment.status === 'error') {
          <button class="ai-chat-attachment-retry" type="button"
            (click)="retryAttachment(attachment)"
            [attr.aria-label]="'Retry uploading ' + attachment.file.name" ngbTooltip="Retry upload"
            placement="top" container="body">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 11.5a8 8 0 1 1-2.6-5.4M20 4v5h-5" />
            </svg>
          </button>
          }
          <button class="ai-chat-attachment-remove" type="button"
            (click)="removeAttachment(attachment)"
            [attr.aria-label]="'Remove ' + attachment.file.name" ngbTooltip="Remove image"
            placement="top" container="body">&times;</button>
        </div>
        }
      </div>
      }
      @if (attachError) {
      <p class="ai-chat-attach-error" role="status">{{ attachError }}</p>
      }

      <!-- TWO ROWS, not one: the draft runs the full width of the field and the controls sit on
           their own row beneath it. A single row put the textarea between the two buttons, which
           cost it ~80px of width on the narrowest screens and pushed its scrollbar into the
           middle of the white box. -->
      <div class="ai-chat-composer-field">
        <label class="visually-hidden" for="ai-chat-input">Your message</label>
        <!-- Kept out of the tab order: the visible button below is the control. -->
        <input class="visually-hidden" #fileInput type="file" multiple tabindex="-1"
          [accept]="attachmentAccept" (change)="onFilesPicked($event)" aria-hidden="true">
        <!-- (paste) is the screenshot path: images on the clipboard become attachments via the
             same intake as the picker and the drop. -->
        <textarea class="ai-chat-input" id="ai-chat-input" #composerInput rows="1"
          placeholder="Ask me anything about Natural Stone…" [(ngModel)]="draftMessage"
          name="draftMessage" [maxlength]="maxMessageLength" (keydown)="onComposerKeydown($event)"
          (input)="onComposerInput()" (paste)="onComposerPaste($event)"
          [disabled]="!!rateLimitNotice()"></textarea>
        <div class="ai-chat-composer-actions">
          <button class="ai-chat-attach-btn" type="button" (click)="openFilePicker()"
            [disabled]="!canAddAttachments() || !!rateLimitNotice()"
            [attr.aria-label]="'Attach an image (up to ' + maxAttachments + ')'"
            ngbTooltip="Attach an image" placement="top" container="body">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          @if (isAwaitingReply()) {
          <button class="ai-chat-send-btn is-stop" type="button" (click)="stopGenerating()"
            aria-label="Stop generating" ngbTooltip="Stop generating" placement="top"
            container="body">
            <span class="ai-chat-stop-glyph" aria-hidden="true"></span>
          </button>
          } @else {
          <!-- Inline rather than the arrow asset: the glyph is drawn in currentColor, so the
               disabled button can be a quiet grey chip with a grey arrow instead of a washed-out
               green square with a white arrow disappearing into it. -->
          <button class="ai-chat-send-btn" type="submit"
            [disabled]="(!draftMessage.trim() && !pendingAttachments.length) || isUploadingAttachments() || !!rateLimitNotice()"
            aria-label="Send message" ngbTooltip="Send" placement="top" container="body">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M8 14V2M8 2 2.5 7.5M8 2l5.5 5.5" />
            </svg>
          </button>
          }
        </div>

        <!-- The drop target, shown only while files are over the form. Scenery, not a control:
             pointer-events pass through it (the form's own drop handler does the work) and it is
             aria-hidden - keyboard and screen-reader users have the attach button. -->
        @if (isDragOver) {
        <div class="ai-chat-dropzone" aria-hidden="true">
          <svg class="ai-chat-dropzone-icon" width="22" height="22" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
            stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span class="ai-chat-dropzone-text">Drop images to attach
            <span class="ai-chat-dropzone-hint">PNG, JPG, WEBP or GIF &middot; up to {{ maxAttachments }}</span>
          </span>
        </div>
        }
      </div>
    </form>
    <p class="ai-chat-disclaimer">Responses are AI generated. Confirm critical details with our team.</p>
    } @else {
    <!-- Signed out: the panel holds only the way in - no transcript, starters or composer until
         there is an account to talk to. Signing in turns this same panel into the chat in place
         (see AiChatComponent's isUserLoggedIn subscription). -->
    <div class="ai-chat-guest">
      <div class="ai-chat-guest-card">
        <span class="ai-chat-guest-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
        </span>
        <div class="ai-chat-guest-copy">
          <h2 class="ai-chat-guest-title">Please sign in to use Pebbl AI</h2>
          <p class="ai-chat-guest-text">Pebbl is GatherCo's AI powered assistant for trade account
            holders. Sign in to start a conversation and Pebbl will be ready to assist you.</p>
        </div>
        <button class="ai-chat-guest-signin" #guestSignIn type="button"
          (click)="openAuthModal(AUTH_MODAL_TYPES.LOGIN)">Sign In</button>
        <p class="ai-chat-guest-register">New to GatherCo?
          <button class="ai-chat-guest-link" type="button"
            (click)="openAuthModal(AUTH_MODAL_TYPES.REGISTER)">Create a Trade Account</button>
        </p>
      </div>
    </div>
    }
  </section>
  }
  <!--============================ Chat Panel End ============================-->

  <!--============================ Toggle Start ============================-->
  <!-- A tab bound to the right edge, glyph over a vertical label. Opening slides the panel out of
       the same edge and the tab rides out on its left side as the Close handle - see
       .ai-chat-toggle. -->
  <button class="ai-chat-toggle" #toggleButton type="button" (click)="toggleChat()"
    [class.is-open]="isOpen()" [attr.aria-expanded]="isOpen()" aria-controls="ai-chat-panel"
    [attr.aria-label]="isOpen() ? 'Close AI support chat' : 'Open AI support chat'"
    ngbTooltip="Close chat" placement="left" container="body" [disableTooltip]="!isOpen()">
    <span class="ai-chat-toggle-sweep" aria-hidden="true"></span>
    <span class="ai-chat-toggle-icon">
      @if (isOpen()) {
      <!-- A chevron pointing back at the edge the panel tucks into, not a cross: the header
           already carries the panel's cross, and this is the handle that pushes it away.
           currentColor keeps it the tab's own ink wherever the ground goes next. -->
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
      } @else {
      <!-- Same Pebbl spark the console uses for its /ai entry point, so the assistant reads as one
           product across the two apps. -->
      <!-- viewBox is padded a unit past the glyph so the breathe animation's peak (stroke
           included) never clips against the SVG edge. -->
      <!-- ONE DUOTONE, THE SAME DUOTONE EVERYWHERE - and only half of it set here. The star
           rides currentColor, so it takes the tile's own ink: white on the solid green tile,
           through rest, sweep and close alike. The two sparkles are pinned $pebblBrass by the
           stylesheet - in BOTH states, which is safe where the old all-one-ink rule had to
           forbid pinning: brass is legible on the green tile, the darker sweep, and the light
           grounds the other marks sit on. The header chip and the panel badge carry the same
           pairing green-on-light, so the marks stay one logo; what must never come back is a
           colour set on the MAIN path. -->
      <svg class="ai-chat-spark" width="30" height="30" viewBox="-1 -1 22 22" fill="none"
        xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="1.6"
        stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path class="ai-chat-spark-main"
          d="M8.14174 13.1499C8.06139 12.8385 7.89905 12.5542 7.67161 12.3268C7.44417 12.0993 7.15993 11.937 6.84847 11.8567L1.32712 10.4329C1.23292 10.4062 1.15001 10.3494 1.09098 10.2713C1.03194 10.1932 1 10.0979 1 10C1 9.90208 1.03194 9.80683 1.09098 9.72871C1.15001 9.65058 1.23292 9.59385 1.32712 9.56711L6.84847 8.14245C7.15982 8.06217 7.44398 7.89997 7.67141 7.67269C7.89885 7.44542 8.06125 7.16137 8.14174 6.85008L9.5655 1.32872C9.59197 1.23414 9.64865 1.15083 9.72689 1.09148C9.80513 1.03213 9.90064 1 9.99884 1C10.097 1 10.1926 1.03213 10.2708 1.09148C10.349 1.15083 10.4057 1.23414 10.4322 1.32872L11.855 6.85008C11.9354 7.16153 12.0977 7.44577 12.3252 7.67321C12.5526 7.90066 12.8369 8.063 13.1483 8.14335L18.6697 9.56621C18.7646 9.5924 18.8483 9.64902 18.908 9.72737C18.9677 9.80573 19 9.90151 19 10C19 10.0985 18.9677 10.1943 18.908 10.2726C18.8483 10.351 18.7646 10.4076 18.6697 10.4338L13.1483 11.8567C12.8369 11.937 12.5526 12.0993 12.3252 12.3268C12.0977 12.5542 11.9354 12.8385 11.855 13.1499L10.4313 18.6713C10.4048 18.7659 10.3481 18.8492 10.2699 18.9085C10.1917 18.9679 10.0961 19 9.99794 19C9.89974 19 9.80423 18.9679 9.72599 18.9085C9.64775 18.8492 9.59107 18.7659 9.5646 18.6713L8.14174 13.1499Z" />
        <path class="ai-chat-spark-minor"
          d="M17.1982 1.9002V5.50011M18.9982 3.70016H15.3982" />
        <path class="ai-chat-spark-minor"
          d="M2.79858 14.4999V16.2998M3.69856 15.3999H1.8986" />
      </svg>
      }
    </span>
    <span class="ai-chat-toggle-label">{{ isOpen() ? 'Close' : 'Ask Pebbl AI' }}</span>
  </button>
  <!--============================ Toggle End ============================-->

</div>
}
<!--================================= AI Chat Widget End =================================-->
